/**
 * Session recovery and persistence for authentication flow
 */

import type { AuthPhase } from '@/types/profileTypes';
import { profileStorage } from '@/services/profileStorage';
import {
  saveEnhancedAuthState,
  loadEnhancedAuthState,
  clearEnhancedAuthState,
  validateAuthState
} from '@/utils/auth-state-persistence';
import type { AuthOrchestratorState } from './useAuthState';
import { STORAGE_KEYS } from '../constants';
import { createDebugLogger } from '../utils/debug';

const logger = createDebugLogger('AuthRecovery');

// Use constants from shared file
const PROFILE_PHASE_STORAGE_KEY = STORAGE_KEYS.PROFILE_PHASE;
const PROFILE_PHASE_TIMESTAMP_KEY = STORAGE_KEYS.PROFILE_TIMESTAMP;

// Interface for recovery state with timestamp
interface RecoveryStateWithTimestamp {
  phase: string;
  selectedUserTypes: string[];
  currentRoleIndex: number;
  profileData: Record<string, unknown>;
  contact: string;
  isEmail: boolean;
  timestamp?: number | string;
  [key: string]: unknown;
}

export function useAuthRecovery(state: AuthOrchestratorState) {
  /**
   * Save current profile state to storage for recovery
   */
  const saveProfileStateToStorage = () => {
    try {
      // Use enhanced persistence for better state tracking
      const success = saveEnhancedAuthState(state);
      if (!success) {
        logger.error('Enhanced state save failed, falling back to basic save');
        // Fallback to basic save for compatibility
        const stateToSave = {
          phase: state.phase,
          selectedUserTypes: state.selectedUserTypes,
          currentRoleIndex: state.currentRoleIndex,
          profileData: state.profileData,
          contact: state.contact,
          isEmail: state.isEmail
        };
        localStorage.setItem(PROFILE_PHASE_STORAGE_KEY, JSON.stringify(stateToSave));
        localStorage.setItem(PROFILE_PHASE_TIMESTAMP_KEY, new Date().toISOString());
      } else {
        logger.log('Enhanced auth state saved successfully');
      }
    } catch (e) {
      logger.error('Failed to save profile state:', e);
    }
  };

  /**
   * Clear stored profile state
   */
  const clearStoredProfileState = () => {
    // Clear both enhanced and legacy storage
    clearEnhancedAuthState();
    localStorage.removeItem(PROFILE_PHASE_STORAGE_KEY);
    localStorage.removeItem(PROFILE_PHASE_TIMESTAMP_KEY);
    logger.log('All stored profile state cleared');
  };

  /**
   * Check if the stored state is still valid
   */
  const isStateStillValid = (stateToValidate: unknown): boolean => {
    if (!stateToValidate || typeof stateToValidate !== 'object') return false;

    const validatedState = stateToValidate as RecoveryStateWithTimestamp;

    // Check if we have the minimum required fields
    const requiredFields = ['phase', 'selectedUserTypes', 'profileData'];
    const missingField = requiredFields.find(field => !(field in validatedState));
    if (missingField) {
      logger.log(`Missing required field: ${missingField}`);
      return false;
    }

    // Check if phase is valid
    const validPhases: AuthPhase[] = [
      'idle',
      'contact',
      'otp',
      'password',
      'setPassword',
      'alternateContact',
      'accountDiscovery',
      'identityConflict',
      'identityLinkingOtp',
      'generalProfile',
      'teacherProfile',
      'studentProfile',
      'parentProfile',
      'employerProfile',
      'schoolProfile',
      'organizerProfile',
      'complete',
      'error'
    ];

    if (!validPhases.includes(validatedState.phase as AuthPhase)) {
      logger.log(`Invalid phase: ${validatedState.phase}`);
      return false;
    }

    // Validate timestamps if present
    if (validatedState.timestamp) {
      const storedTime = new Date(validatedState.timestamp).getTime();
      const currentTime = new Date().getTime();
      const hoursSinceStored = (currentTime - storedTime) / (1000 * 60 * 60);

      // Invalidate if older than 24 hours
      if (hoursSinceStored > 24) {
        logger.log('Stored state is older than 24 hours');
        return false;
      }
    }

    return true;
  };

  /**
   * Load profile state from storage
   */
  const loadProfileStateFromStorage = () => {
    try {
      // First try to load enhanced state
      const enhancedState = loadEnhancedAuthState();
      if (enhancedState && validateAuthState(enhancedState)) {
        logger.log('Loaded enhanced auth state successfully');

        // Apply recovered state with complete profile data
        Object.assign(state, {
          phase: enhancedState.phase,
          previousPhase: enhancedState.previousPhase || 'idle',
          phaseHistory: enhancedState.phaseHistory || [],
          completedPhases: enhancedState.completedPhases || new Set(),
          maxReachedPhase: enhancedState.maxReachedPhase || 'idle',
          encounteredPhases: enhancedState.encounteredPhases || new Set(),
          criticalDecisions: enhancedState.criticalDecisions || new Map(),
          lockedPhases: enhancedState.lockedPhases || new Set(),
          conditionalTriggers: enhancedState.conditionalTriggers || new Map(),
          passedGates: enhancedState.passedGates || new Set(),
          resolvedConflicts: enhancedState.resolvedConflicts || new Set(),
          profileData: {
            general: enhancedState.profileData?.general || null,
            teacher: enhancedState.profileData?.teacher || null,
            student: enhancedState.profileData?.student || null,
            parent: enhancedState.profileData?.parent || null,
            employer: enhancedState.profileData?.employer || null,
            school: enhancedState.profileData?.school || null,
            organizer: enhancedState.profileData?.organizer || null
          },
          contact: enhancedState.contact || '',
          isEmail: enhancedState.isEmail !== undefined ? enhancedState.isEmail : true,
          selectedUserTypes: enhancedState.selectedUserTypes || [],
          currentRoleIndex: enhancedState.currentRoleIndex || 0,
          pendingIdentityLink: enhancedState.pendingIdentityLink || {
            type: null,
            value: '',
            otpSent: false
          },
          alternateContactSkipped: enhancedState.alternateContactSkipped || false,
          discoveredAccountId: enhancedState.discoveredAccountId || null,
          portfolioCompletionStatus: enhancedState.portfolioCompletionStatus || null
        });

        return enhancedState;
      }

      // Fallback to legacy storage
      const storedState = localStorage.getItem(PROFILE_PHASE_STORAGE_KEY);
      const storedTimestamp = localStorage.getItem(PROFILE_PHASE_TIMESTAMP_KEY);

      if (!storedState || !storedTimestamp) {
        return null;
      }

      const parsedState = JSON.parse(storedState);
      const storedTime = new Date(storedTimestamp).getTime();
      const currentTime = new Date().getTime();
      const hoursSinceStored = (currentTime - storedTime) / (1000 * 60 * 60);

      // Only restore if less than 24 hours old
      if (hoursSinceStored > 24) {
        logger.log('Stored state is older than 24 hours, clearing');
        clearStoredProfileState();
        return null;
      }

      // Validate the stored state
      if (!isStateStillValid(parsedState)) {
        logger.log('Stored state is invalid, clearing');
        clearStoredProfileState();
        return null;
      }

      logger.log('Loaded profile state from storage:', parsedState);
      return parsedState;
    } catch (e) {
      logger.error('Failed to load profile state:', e);
      clearStoredProfileState();
      return null;
    }
  };

  /**
   * Check if recovery is available
   */
  const checkForRecovery = (): boolean => {
    const storedState = loadProfileStateFromStorage();
    if (storedState && storedState.phase !== 'idle' && storedState.phase !== 'complete') {
      // Check for non-recoverable phases
      const nonRecoverablePhases = [
        'otp',
        'password',
        'passwordSignup',
        'setPassword',
        'identityLinkingOtp',
        'accountDiscovery',
        'identityConflict'
      ];

      if (nonRecoverablePhases.includes(storedState.phase)) {
        logger.warn(`Cannot recover from ${storedState.phase} - clearing invalid state`);
        clearStoredProfileState();
        return false;
      }

      // Special validation for alternateContact
      if (storedState.phase === 'alternateContact') {
        // Don't allow recovery if discovery or linking is in progress
        if (storedState.discoveredAccountId || storedState.pendingIdentityLink?.type) {
          logger.warn('Cannot recover to alternateContact with pending operations');
          // Redirect to a safe phase instead
          storedState.phase = 'generalProfile';
        }
      }

      state.recoveryAvailable = true;
      return true;
    }
    state.recoveryAvailable = false;
    return false;
  };

  /**
   * Recover the session from stored state
   */
  const recoverSession = () => {
    const storedState = loadProfileStateFromStorage();
    if (!storedState) {
      logger.log('No valid stored state to recover');
      return;
    }

    // Validate the phase before recovering
    const nonRecoverablePhases = [
      'otp',
      'password',
      'passwordSignup',
      'setPassword',
      'identityLinkingOtp',
      'accountDiscovery',
      'identityConflict'
    ];

    // Check for state consistency issues
    if (storedState.passedGates?.size > 0) {
      // Verify we're not trying to recover to a gate we've already passed
      const passedGatesArray = Array.from(storedState.passedGates || []);
      if (passedGatesArray.includes(storedState.phase)) {
        logger.warn(`Cannot recover to ${storedState.phase} - already passed this one-way gate`);
        storedState.phase = 'generalProfile'; // Safe fallback
      }
    }

    if (nonRecoverablePhases.includes(storedState.phase)) {
      logger.warn(`Cannot recover from ${storedState.phase} phase - finding safe fallback`);

      // Determine safe fallback phase
      let fallbackPhase: AuthPhase = 'generalProfile';

      // If user hasn't completed alternate contact and hasn't skipped it
      // AND there's no pending discovery/linking operation
      if (
        !storedState.alternateContactSkipped &&
        !storedState.profileData?.general?.phoneNumber &&
        !storedState.profileData?.general?.email &&
        !storedState.discoveredAccountId &&
        !storedState.pendingIdentityLink?.type
      ) {
        fallbackPhase = 'alternateContact';
      }
      // If general profile not complete, go there
      else if (!storedState.profileData?.general) {
        fallbackPhase = 'generalProfile';
      }
      // If in middle of role profiles, continue from there
      else if (storedState.currentRoleIndex < storedState.selectedUserTypes?.length) {
        const roleType = storedState.selectedUserTypes[storedState.currentRoleIndex]?.toLowerCase();
        if (roleType) {
          fallbackPhase = `${roleType}Profile` as AuthPhase;
        }
      }

      // Update the phase to the safe fallback
      storedState.phase = fallbackPhase;
      logger.log(`Redirecting recovery to safe phase: ${fallbackPhase}`);
    }

    // Additional validation for alternateContact
    if (storedState.phase === 'alternateContact') {
      // Check if there's a pending operation that makes alternateContact invalid
      if (storedState.discoveredAccountId || storedState.pendingIdentityLink?.type) {
        logger.warn('AlternateContact has pending operations, redirecting to generalProfile');
        storedState.phase = 'generalProfile';
      }
    }

    logger.log('Recovering session from phase:', storedState.phase);

    // Apply the stored state with complete profile data
    Object.assign(state, {
      phase: storedState.phase,
      selectedUserTypes: storedState.selectedUserTypes || [],
      currentRoleIndex: storedState.currentRoleIndex || 0,
      profileData: {
        general: storedState.profileData?.general || null,
        teacher: storedState.profileData?.teacher || null,
        student: storedState.profileData?.student || null,
        parent: storedState.profileData?.parent || null,
        employer: storedState.profileData?.employer || null,
        school: storedState.profileData?.school || null,
        organizer: storedState.profileData?.organizer || null
      },
      contact: storedState.contact || '',
      isEmail: storedState.isEmail !== undefined ? storedState.isEmail : true,
      recoveryAvailable: false,
      isOpen: true
    });

    // If we have enhanced state, apply additional fields
    if (storedState.phaseHistory) {
      state.phaseHistory = storedState.phaseHistory;
    }
    if (storedState.completedPhases) {
      state.completedPhases = storedState.completedPhases;
    }
    if (storedState.encounteredPhases) {
      state.encounteredPhases = storedState.encounteredPhases;
    }
    if (storedState.previousPhase) {
      state.previousPhase = storedState.previousPhase;
    }
    if (storedState.portfolioCompletionStatus) {
      state.portfolioCompletionStatus = storedState.portfolioCompletionStatus;
    }

    logger.log('Session recovered successfully');
  };

  return {
    saveProfileStateToStorage,
    clearStoredProfileState,
    loadProfileStateFromStorage,
    isStateStillValid,
    checkForRecovery,
    recoverSession
  };
}
